import React from 'react';
import styled from 'styled-components';
import { makeStyles, LinearProgress } from '@material-ui/core';
import { black, gray } from '@avangard/ui/colors';

import type { Theme } from '@material-ui/core';

type ProgressItemProps = {
    label: string;
    valueLabel: string;
    value: number;
    maxValue: number;
    lineColor: string;
};

const useLinearProgressStyles = makeStyles<Theme, { color: string }>({
    root: {
        borderRadius: 4,
    },

    colorPrimary: {
        backgroundColor: gray[30],
    },

    barColorPrimary: p => ({
        backgroundColor: p.color,
    }),
});

const Root = styled.div`
    width: 100%;
`;

const Header = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    margin-bottom: 8px;
    color: ${black[54]};

    > h5 {
        font-size: 1.2rem;
        font-weight: 400;
    }

    > p {
        margin-inline-start: auto;
        font-size: 1.2rem;
    }
`;

const Main = styled.div`
    width: 100%;
`;

const normalise = (value: number, max: number, min = 0) => ((value - min) * 100) / (max - min);

const ProgressItem = (props: ProgressItemProps) => {
    const { label, valueLabel, value, maxValue, lineColor } = props;

    const linearProgressClasses = useLinearProgressStyles({ color: lineColor });

    const [progress, setProgress] = React.useState(0);

    const normalisedScore = normalise(value, maxValue);

    React.useEffect(() => {
        const timer = setInterval(() => {
            setProgress(state => (state < normalisedScore ? state + 20 : normalisedScore));
        }, 100);

        return () => {
            clearInterval(timer);
        };
    }, [normalisedScore]);

    return (
        <Root>
            <Header>
                <h5>{label}</h5>
                <p>{valueLabel}</p>
            </Header>

            <Main>
                <LinearProgress
                    variant='determinate'
                    classes={linearProgressClasses}
                    value={progress}
                />
            </Main>
        </Root>
    );
};

export { ProgressItem };
